(() => {
  window.Express = window.Express || {};

  const attachedElements = new WeakMap();

  function isEnabled(maxWidth) {
    if (!maxWidth) return true;
    return window.matchMedia?.(`(max-width: ${maxWidth}px)`).matches ?? true;
  }

  function getDirection(deltaX) {
    if (deltaX === 0) return 0;
    return deltaX < 0 ? 1 : -1;
  }

  function createPayload(event, state) {
    const deltaX = state.currentX - state.startX;
    const deltaY = state.currentY - state.startY;

    return {
      absX: Math.abs(deltaX),
      absY: Math.abs(deltaY),
      deltaX,
      deltaY,
      direction: getDirection(deltaX),
      dragged: state.dragged,
      event,
      pointerType: event?.pointerType || '',
    };
  }

  function attach(element, options = {}) {
    if (!element) return null;
    const existing = attachedElements.get(element);
    if (existing) return existing;

    const settings = {
      allowTouch: Boolean(options.allowTouch),
      cancelOnOutsidePointerDown: Boolean(options.cancelOnOutsidePointerDown),
      cancelOnVertical: options.cancelOnVertical !== false,
      capturePointer: options.capturePointer !== false,
      draggingClass: options.draggingClass || 'is-dragging',
      maxWidth: Number(options.maxWidth) || 0,
      preventDefault: options.preventDefault !== false,
      suppressClick: Boolean(options.suppressClick),
      threshold: Number(options.threshold) || 6,
      touchAction: options.touchAction || '',
    };
    const state = {
      active: false,
      dragged: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      suppressClick: false,
    };

    if (settings.touchAction && !element.style.touchAction) {
      element.style.touchAction = settings.touchAction;
    }

    function resetDrag() {
      state.active = false;
      state.dragged = false;
      state.pointerId = null;
      element.classList.remove(settings.draggingClass);
    }

    function cancelDrag(event) {
      if (!state.active) return;
      if (event?.pointerId !== undefined && event.pointerId !== state.pointerId) return;
      const payload = createPayload(event, state);
      resetDrag();
      if (typeof options.onCancel === 'function') {
        options.onCancel(payload);
      }
    }

    function canStart(event) {
      if (!isEnabled(settings.maxWidth)) return false;
      if (!settings.allowTouch && event.pointerType === 'touch') return false;
      if (event.button !== undefined && event.button !== 0) return false;
      if (typeof options.shouldStart === 'function' && options.shouldStart(event) === false) return false;
      return true;
    }

    function handlePointerDown(event) {
      if (!canStart(event)) return;

      state.active = true;
      state.dragged = false;
      state.pointerId = event.pointerId;
      state.startX = event.clientX;
      state.startY = event.clientY;
      state.currentX = event.clientX;
      state.currentY = event.clientY;

      if (settings.capturePointer && typeof element.setPointerCapture === 'function') {
        try {
          element.setPointerCapture(event.pointerId);
        } catch (_error) {
          // Pointer capture can fail if the browser has already cancelled the pointer.
        }
      }

      if (typeof options.onStart === 'function') {
        options.onStart(createPayload(event, state));
      }
    }

    function handlePointerMove(event) {
      if (!state.active || event.pointerId !== state.pointerId) return;

      state.currentX = event.clientX;
      state.currentY = event.clientY;

      const payload = createPayload(event, state);
      if (!state.dragged && Math.max(payload.absX, payload.absY) < settings.threshold) return;
      if (!state.dragged && settings.cancelOnVertical && payload.absY > payload.absX) {
        cancelDrag(event);
        return;
      }

      state.dragged = true;
      if (settings.suppressClick) state.suppressClick = true;
      element.classList.add(settings.draggingClass);

      if (typeof options.onMove === 'function') {
        options.onMove(createPayload(event, state));
      }

      if (settings.preventDefault && event.cancelable) event.preventDefault();
    }

    function handlePointerUp(event) {
      if (event.pointerId !== state.pointerId) return;
      const payload = createPayload(event, state);

      if (state.dragged && settings.suppressClick) {
        state.suppressClick = true;
        window.setTimeout(() => {
          state.suppressClick = false;
        }, 250);
      }

      resetDrag();

      if (typeof options.onEnd === 'function') {
        options.onEnd(payload);
      }
    }

    function handleOutsidePointerDown(event) {
      if (!settings.cancelOnOutsidePointerDown || !state.active || element.contains(event.target)) return;
      cancelDrag(event);
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') cancelDrag(null);
    }

    function handleClick(event) {
      if (!settings.suppressClick || !state.suppressClick) return;
      state.suppressClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    element.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('pointermove', handlePointerMove, { passive: false });
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', cancelDrag);
    window.addEventListener('blur', cancelDrag);
    document.addEventListener('pointerdown', handleOutsidePointerDown, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    element.addEventListener('click', handleClick, true);

    const controller = {
      destroy() {
        element.removeEventListener('pointerdown', handlePointerDown);
        window.removeEventListener('pointermove', handlePointerMove, { passive: false });
        window.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('pointercancel', cancelDrag);
        window.removeEventListener('blur', cancelDrag);
        document.removeEventListener('pointerdown', handleOutsidePointerDown, true);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        element.removeEventListener('click', handleClick, true);
        attachedElements.delete(element);
      },
    };

    attachedElements.set(element, controller);
    return controller;
  }

  window.Express.productBuilderHorizontalDrag = {
    attach,
  };
})();
